// Responsibility boundary:
// - owns Voximplant Call event subscription/unsubscription for the videocall feature
// - translates SDK call events into UI/controller callbacks
// - does not touch DOM or React component state directly
import * as VoxImplant from 'voximplant-websdk';
import {Call} from 'voximplant-websdk/Call/Call';
import {Logger} from '../../utils/managers/logger';
import {EndpointEvent} from './videocall-endpoints';

type VoxCallEvent = unknown;
type VoxCallFailedEvent = {reason: string; code: number};

export type VideocallCallEventHandlers = {
  updated: (e: VoxCallEvent) => void;
  progressToneStart: () => void;
  progressToneStop: () => void;
  connected: () => void;
  disconnected: () => void;
  failed: (e: VoxCallFailedEvent) => void;
  mediaElementCreated: (e: VoxCallEvent) => void;
};

export type VideocallCallEventHandlersMap = WeakMap<Call, VideocallCallEventHandlers>;

export type VideocallSetUpCallParams = {
  currentCall: Call;
  isIncoming: boolean;
  number: string;
  viewer: boolean;
  video: boolean;
};

type SharedDependencies = {
  call: Call;
  handlersMap: VideocallCallEventHandlersMap;
  onEndpointAdded: (data: EndpointEvent) => void;
};

type AttachDependencies = {
  logger: Logger;
  simpleStringify: (object: VoxCallEvent) => string;
  startTimer: () => void;
  stopCallTimer: () => void;
  callStateConnected: (video: boolean) => void;
  callStateDisconnected: (call: Call) => void;
  openCallFailed: (code: number, reason: string) => void;
  establishingCallAudio: HTMLAudioElement;
  callingAudio: HTMLAudioElement;
  callNotMadeAudio: HTMLAudioElement;
};

export function detachCallListeners({call, handlersMap, onEndpointAdded}: SharedDependencies) {
  const handlers = handlersMap.get(call);
  call.off(VoxImplant.CallEvents.EndpointAdded, onEndpointAdded);

  if (!handlers) {
    return;
  }

  call.off(VoxImplant.CallEvents.Updated, handlers.updated);
  call.off(VoxImplant.CallEvents.ProgressToneStart, handlers.progressToneStart);
  call.off(VoxImplant.CallEvents.ProgressToneStop, handlers.progressToneStop);
  call.off(VoxImplant.CallEvents.Connected, handlers.connected);
  call.off(VoxImplant.CallEvents.Disconnected, handlers.disconnected);
  call.off(VoxImplant.CallEvents.Failed, handlers.failed);
  call.off(VoxImplant.CallEvents.MediaElementCreated, handlers.mediaElementCreated);
  handlersMap.delete(call);
}

export function attachCallListeners(
  callSetup: VideocallSetUpCallParams,
  deps: AttachDependencies & {
    handlersMap: VideocallCallEventHandlersMap;
    onEndpointAdded: (data: EndpointEvent) => void;
  }
) {
  let prefix = callSetup.currentCall.settings.isConference ? 'Call conference to' : 'Call to';

  if (callSetup.isIncoming) {
    prefix = 'Incoming call from';
  } else {
    deps.establishingCallAudio.loop = true;
    deps.establishingCallAudio.play();
  }

  deps.logger.debug(`${prefix} ${callSetup.number}...`);

  const handlers: VideocallCallEventHandlers = {
    updated: (e) => {
      deps.logger.debug(`CALL UPDATED: ${deps.simpleStringify(e)}`);
    },
    progressToneStart: () => {
      deps.establishingCallAudio.pause();
      deps.establishingCallAudio.currentTime = 0;
      deps.callingAudio.loop = true;
      deps.callingAudio.play();
    },
    progressToneStop: () => {
      deps.establishingCallAudio.pause();
      deps.establishingCallAudio.currentTime = 0;
      deps.callingAudio.pause();
      deps.callingAudio.currentTime = 0;
    },
    connected: () => {
      deps.startTimer();

      if (callSetup.isIncoming) {
        deps.callStateConnected(true);
      } else if (!callSetup.viewer) {
        deps.callStateConnected(callSetup.video);
      }
    },
    disconnected: () => {
      deps.logger.debug(`${prefix} ${callSetup.number} was disconnected.`);
      deps.callStateDisconnected(callSetup.currentCall);
    },
    failed: (e) => {
      deps.logger.debug(`${prefix} ${callSetup.number} failed: ${e.reason} (${e.code})`);
      deps.establishingCallAudio.pause();
      deps.establishingCallAudio.currentTime = 0;
      deps.callingAudio.pause();
      deps.callingAudio.currentTime = 0;
      deps.stopCallTimer();
      deps.callNotMadeAudio.play();
      deps.callStateDisconnected(callSetup.currentCall);
      deps.openCallFailed(e.code, e.reason);
    },
    mediaElementCreated: (e) => {
      deps.logger.debug(`CALL MediaElementCreated: ${deps.simpleStringify(e)}`);
    },
  };

  detachCallListeners({
    call: callSetup.currentCall,
    handlersMap: deps.handlersMap,
    onEndpointAdded: deps.onEndpointAdded,
  });
  deps.handlersMap.set(callSetup.currentCall, handlers);

  callSetup.currentCall.on(VoxImplant.CallEvents.EndpointAdded, deps.onEndpointAdded);
  callSetup.currentCall.on(VoxImplant.CallEvents.Updated, handlers.updated);
  callSetup.currentCall.on(VoxImplant.CallEvents.ProgressToneStart, handlers.progressToneStart);
  callSetup.currentCall.on(VoxImplant.CallEvents.ProgressToneStop, handlers.progressToneStop);
  callSetup.currentCall.on(VoxImplant.CallEvents.Connected, handlers.connected);
  callSetup.currentCall.on(VoxImplant.CallEvents.Disconnected, handlers.disconnected);
  callSetup.currentCall.on(VoxImplant.CallEvents.Failed, handlers.failed);
  callSetup.currentCall.on(VoxImplant.CallEvents.MediaElementCreated, handlers.mediaElementCreated);
}
